/**
 * Coach marks contextuels — orchestration "1 bulle à la fois".
 * Chaque <Coachmark> déclare sa disponibilité (ancre montée + condition + non "vu") ;
 * le provider n'active QUE la première selon l'ordre de priorité (COACHMARK_ORDER).
 * Le statut onboarding (dont coachmarksSeen) est chargé une seule fois ici et partagé.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useOnboardingStatus, useOnboardingPatch } from '@/hooks/useOnboarding';
import type { OnboardingStatus } from '@/types';

// Ordre = priorité d'affichage. Miroir du COACHMARK_IDS backend (source de vérité partagée).
// `emission-siret` est un garde inline (dialog d'émission), pas une bulle : il ne s'enregistre pas ici.
export const COACHMARK_ORDER = [
    'dashboard-checklist',
    'dashboard-forecast',
    'invoices-new',
    'tresorerie-forecast',
    'retention-letter',
] as const;

export type CoachmarkId = (typeof COACHMARK_ORDER)[number];

interface CoachmarkContextValue {
    status: OnboardingStatus | undefined;
    activeId: CoachmarkId | null;
    setReady: (id: CoachmarkId, ready: boolean) => void;
    markSeen: (id: CoachmarkId) => void;
}

const CoachmarkContext = createContext<CoachmarkContextValue | null>(null);

export function CoachmarkProvider({ children }: { children: ReactNode }): ReactNode {
    const { data: status } = useOnboardingStatus();
    const patch = useOnboardingPatch();
    const [ready, setReadyState] = useState<Record<string, boolean>>({});

    const setReady = useCallback((id: CoachmarkId, isReady: boolean) => {
        setReadyState((prev) => (Boolean(prev[id]) === isReady ? prev : { ...prev, [id]: isReady }));
    }, []);

    const markSeen = useCallback(
        (id: CoachmarkId) => patch.mutate({ action: 'markCoachmark', coachmarkId: id }),
        [patch]
    );

    const activeId = useMemo<CoachmarkId | null>(
        () => COACHMARK_ORDER.find((id) => ready[id]) ?? null,
        [ready]
    );

    const value = useMemo<CoachmarkContextValue>(
        () => ({ status, activeId, setReady, markSeen }),
        [status, activeId, setReady, markSeen]
    );

    return <CoachmarkContext.Provider value={value}>{children}</CoachmarkContext.Provider>;
}

export function useCoachmarkContext(): CoachmarkContextValue {
    const ctx = useContext(CoachmarkContext);
    if (!ctx) throw new Error('useCoachmarkContext doit être utilisé dans un CoachmarkProvider');
    return ctx;
}
